import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { baseApi, contentDispositionFilename } from '@/shared/api'
import type {
  PersonalPriceAccepted,
  PriceCommand,
  PriceFile,
  PriceFormat,
  PriceKind,
} from '../model/types'

/**
 * Темп опроса команды сборки персонального прайса, пока 1С не ответила и срок не вышел.
 * Запасной путь: результат опережает событие `command.updated`/`price.personal.ready`, которое
 * §0 доставляет сокетом и инвалидирует тег `Command` — тогда рефетч приходит раньше опроса.
 */
export const PERSONAL_PRICE_POLL_MS = 10_000

/**
 * Бинарный ответ ручки: успех — поток файла (`blob`), иначе — тело `ApiErrorDto` (JSON), чтобы
 * `normalizeError` разобрал код и статус. Один обработчик на все выгрузки прайса.
 */
const fileOrError = async (response: Response): Promise<Blob | unknown> =>
  response.ok ? response.blob() : response.json()

/** Имя файла из ответа; нет доступного `Content-Disposition` — имя по контракту (`fallback`). */
function fileFrom(raw: unknown, meta: FetchBaseQueryMeta | undefined, fallback: string): PriceFile {
  // Граница с бинарным ответом: на успехе `fileOrError` вернул Blob (ветка ошибки сюда не доходит)
  const blob = raw as Blob
  const header = meta?.response?.headers.get('content-disposition')
  return { blob, filename: contentDispositionFilename(header) ?? fallback }
}

export const priceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Смена ценовой колонки сеанса (`POST /v1/prices/column`). Живёт в сессии, влияет на грид
     * сборки и персональный прайс. Текущей колонки ни `/auth/me`, ни отдельная ручка не отдают
     * (`BE-159`) — значение не кешируется, селектор управляется локально. Инвалидация не нужна:
     * серверных данных прайса в кеше фронта нет.
     */
    switchPriceColumn: build.mutation<void, number>({
      query: (column) => ({ url: '/prices/column', method: 'POST', body: { column } }),
    }),

    /** Запрос персонального прайса (`POST /v1/prices/personal`): `202 { commandId }`, дальше опрос. */
    requestPersonalPrice: build.mutation<PersonalPriceAccepted, void>({
      query: () => ({ url: '/prices/personal', method: 'POST' }),
    }),

    /**
     * Состояние команды сборки (`GET /v1/commands/{id}`). Тег `Command` — тот же, что
     * инвалидирует §0 по WS-событию: рефетч приходит сокетом, опрос остаётся запасным путём.
     */
    getPersonalPriceCommand: build.query<PriceCommand, string>({
      query: (id) => ({ url: `/commands/${encodeURIComponent(id)}` }),
      providesTags: (_res, _err, id) => [{ type: 'Command', id }],
    }),

    /**
     * Скачать собранный персональный прайс (`GET /v1/prices/personal/{commandId}/file`) — один
     * раз: после отдачи файл удаляется, повтор → `404`. Поток файла, не кеш: мутация, результат
     * потребитель сохраняет и сбрасывает.
     */
    downloadPersonalPrice: build.mutation<PriceFile, string>({
      query: (commandId) => ({
        url: `/prices/personal/${encodeURIComponent(commandId)}/file`,
        responseHandler: fileOrError,
        cache: 'no-store',
      }),
      transformResponse: (raw, meta) => fileFrom(raw, meta, 'personal-price.xlsx'),
    }),

    /**
     * Скачать готовый прайс-лист (`GET /v1/prices/{kind}/{format}`). `404` — нет данных по виду;
     * безналичные виды закрыты `price.br.view`/`price.bs.view` (`403`). Поток файла — мутация.
     */
    downloadReadyPrice: build.mutation<PriceFile, { kind: PriceKind; format: PriceFormat }>({
      query: ({ kind, format }) => ({
        url: `/prices/${kind}/${format}`,
        responseHandler: fileOrError,
        cache: 'no-store',
      }),
      transformResponse: (raw, meta, arg) =>
        fileFrom(raw, meta, `price-${arg.kind}.${arg.format === 'xls' ? 'xls' : 'zip'}`),
    }),

    /**
     * Прайс с кодами (`GET /v1/prices/export/with-codes`): CSV в WINDOWS-1251 с `\r\n`. Байты
     * ответа не декодируются — `blob` сохраняется как пришёл. `403` — ключ выгрузки не настроен.
     */
    downloadPriceWithCodes: build.mutation<PriceFile, void>({
      query: () => ({
        url: '/prices/export/with-codes',
        responseHandler: fileOrError,
        cache: 'no-store',
      }),
      transformResponse: (raw, meta) => fileFrom(raw, meta, 'price-with-codes.csv'),
    }),
  }),
})

export const {
  useSwitchPriceColumnMutation,
  useRequestPersonalPriceMutation,
  useGetPersonalPriceCommandQuery,
  useDownloadPersonalPriceMutation,
  useDownloadReadyPriceMutation,
  useDownloadPriceWithCodesMutation,
} = priceApi
